/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';
import FineUploaderS3 from 'fine-uploader-wrappers/s3';
import classnames from 'classnames';
import {
  Input,
  UncontrolledTooltip,
  Button,
} from 'reactstrap';
import { injectIntl, useIntl } from 'react-intl';

import { showModal } from '../../../actions';
import {
  createFileVersion,
  deleteFileVersion,
  restoreFileVersion,
} from '../../../actions/Files';

import FileSidebarEmptyState from './EmptyState';

import { getAPIUrl } from '../../../helpers/App';
import { userCanSeeVersions } from '../../../helpers/Auth';
import { getFormattedDateTime } from '../../../helpers/DateTime';
import {
  getFileExtension,
  isImageFile,
} from '../../../helpers/Files';
import { addFlashMessage } from '../../../helpers/FlashMessages';
import { getSetting } from '../../../helpers/Settings';

const FileVersions = ({
  profile,
  file,
  currentVersionId,
  share,
  afterCreateVersion,
  updateCommentsCount,
  switchVersion,
  handleRestoreVersion,
  handleDeleteVersion,
  handleShowCommentsCopy,
  handleCreateVersion,
  readOnly,
}) => {
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(0);
  const [copyComments, setCopyComments] = useState('all');

  const fileInput = useRef(null);
  const uploaderRef = useRef(null);
  const fileMap = useRef({});

  const intl = useIntl();

  const performCreateFileVersion = useCallback((params) => {
    const fileUuid = file.parent_uuid ? file.parent_uuid : file.uuid;

    handleCreateVersion(fileUuid, params)
      .then((response) => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Version has been created' }),
        });
        setUploading(false);
        setUploaded(0);
        fileInput.current.value = null;
        afterCreateVersion(response);
      })
      .catch((error) => {
        setUploading(false);
        setUploaded(0);
        addFlashMessage({
          type: 'danger',
          text: intl.formatMessage({
            defaultMessage: '<strong>Something went wrong.</strong>{br}{error}',
          }, {
            error,
            br: '\n',
            strong: (msg) => <strong>{msg}</strong>,
          }),
        });
        fileInput.current.value = null;
      });
  }, [afterCreateVersion, file.parent_uuid, file.uuid, handleCreateVersion, intl]);

  useEffect(() => {
    uploaderRef.current = new FineUploaderS3({
      options: {
        debug: process.env.MIX_ENV !== 'production',
        multiple: false,
        request: {
          endpoint: `https://${process.env.MIX_AWS_S3_BUCKET_NAME}.s3.amazonaws.com`,
          accessKey: `${getSetting('upload_key') || process.env.MIX_AWS_ACCESS_KEY_ID}`,
        },
        objectProperties: {
          key: (id) => {
            const { name, uuid } = fileMap.current[id] || {};

            const ext = name ? uploaderRef.current.qq.getExtension(name) : '';

            return `${profile.client.uuid}/${uuid?.substring(0, 3)}/${uuid}${ext ? `.${ext}` : ''}`;
          },
        },
        signature: {
          endpoint: getAPIUrl('/upload'),
          version: 4,
          customHeaders: () => ({
            SPAuth: true,
            Authorization: `Bearer ${Cookies.get('sp_access_token')}`,
          }),
        },
        chunking: {
          enabled: true,
          concurrent: {
            enabled: true,
          },
        },
      },
    });

    const uploader = uploaderRef.current;

    uploader.on('submitted', (id, name) => {
      setUploading(true);
      const uuid = uploader.methods.getUuid(id);
      fileMap.current[id] = { name, uuid };

      uploader.methods.setParams({ client: profile.client.uuid }, id);
    });

    uploader.on('progress', (id, name, uploadedBytes, totalBytes) => {
      const percentage = uploadedBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;
      setUploaded(percentage);
    });

    uploader.on('complete', (id, name, response) => {
      if (!response.success) return;

      const newFile = uploader.methods.getFile(id);
      const fileURL = URL.createObjectURL(newFile);
      const fileParams = {
        path: uploader.methods.getKey(id),
        original_name: uploader.methods.getName(id),
        size: newFile.size,
        type: newFile.type,
        copy_comments: copyComments,
      };

      if (isImageFile(newFile.type)) {
        const image = new Image();
        image.src = fileURL;
        image.onload = () => {
          fileParams.width = image.width;
          fileParams.height = image.height;
          performCreateFileVersion(fileParams);
        };
        image.onerror = () => {
          performCreateFileVersion(fileParams);
        };
      } else {
        performCreateFileVersion(fileParams);
      }
    });

    uploader.on('error', (id, name, error) => {
      setUploading(false);
      setUploaded(0);
      addFlashMessage({
        type: 'danger',
        text: intl.formatMessage(
          {
            defaultMessage: '<strong>Something went wrong.</strong>{br}{error}',
          },
          {
            error,
            br: '\n',
            strong: (msg) => <strong>{msg}</strong>,
          },
        ),
      });
    });
  }, [profile, copyComments, intl, performCreateFileVersion]);

  const handleCopyComments = (files, value) => {
    setCopyComments(value);
    setTimeout(() => {
      uploaderRef.current.methods.addFiles(files);
    }, 200);
  };

  const uploadFile = (e) => {
    const { files } = e.target;
    if (files.length > 0) {
      if (file?.comments_count?.all > 0) {
        const modalParams = {
          handleCopyComments,
          commentsCount: file.comments_count,
          commentsCopied: file.comments_copied,
          file,
          files,
          updateCommentsCount,
        };
        handleShowCommentsCopy('create-version', modalParams);
      } else {
        uploaderRef.current.methods.addFiles(files);
      }
    }
  };

  const triggerInputFile = () => {
    if (!uploading) {
      fileInput.current.click();
    }
  };

  const performDeleteFileVersion = (versionId) => {
    if (restoring) return;

    setDeleting(versionId);

    handleDeleteVersion(file.uuid, versionId).then((response) => {
      afterCreateVersion(response);
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'Version has been deleted' }),
      });
      setDeleting(false);
    });
  };

  const performRestoreFileVersion = (versionId) => {
    if (deleting) return;

    setRestoring(versionId);

    handleRestoreVersion(file.uuid, versionId).then((response) => {
      afterCreateVersion(response);
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'Version has been restored' }),
      });
      setRestoring(false);
    });
  };

  if (!(share?.allow_versions || userCanSeeVersions(file))) {
    return null;
  }

  return (
    <div className="file-versions-container file-details-item">
      {(!share && !readOnly) && (
        <div className="file-detail-header">
          <Input
            type="file"
            name="file-version"
            className="upload-file hidden"
            onChange={uploadFile}
            innerRef={fileInput}
            accept={(getSetting('allowed_extensions') || []).map(e => (`.${e}`)).join(',')}
          />
          <Button
            type="button"
            color="link"
            onClick={triggerInputFile}
            className="file-detail-title file-detail-header-button"
          >
            {uploading ? (
              <>
                <i className="fal fa-spinner fa-spin" />
                <span>
                  {intl.formatMessage({ defaultMessage: 'Uploading ({uploaded}%)...' }, { uploaded })}
                </span>
              </>
            ) : (
              <>
                <i className="fal fa-plus" />
                <span>
                  {intl.formatMessage({ defaultMessage: 'Upload a new version of this file' })}
                </span>
              </>
            )}
          </Button>
        </div>
      )}
      <div className="file-detail-header file-detail-header-light">
        <span>{intl.formatMessage({ defaultMessage: 'Versions' })}{' '}{`(${file.versions.length})`}</span>
      </div>
      <div className="file-version-items">
        {file.versions.map((fv, idx) => (
          <div
            key={fv.id}
            className={classnames('file-version-item', { 'file-version-item-active': currentVersionId === fv.id })}
            onClick={() => { switchVersion(fv.id); }}
          >
            <div className="file-version-thumbnail">
              <img src={fv.urls.thumbnail} alt={file.display_name} />
            </div>
            <div className="file-version-details">
              <div className="file-version-meta">
                <span
                  className={classnames('file-version-meta-badge', { 'file-version-meta-badge-show': currentVersionId === fv.id })}
                >
                  {intl.formatMessage({ defaultMessage: `You're viewing this version` })}
                </span>
                <span className="version-date-time">
                  {file.versions[idx + 1] ? (
                    getFormattedDateTime(file.versions[idx + 1].created_at, true)
                  ) : (
                    getFormattedDateTime(file.created_at, true)
                  )}
                </span>
                <span className="file-details">
                  <div>
                    <span className="version-extension">
                      {getFileExtension(fv)}
                    </span>
                    <span className="version-size">
                      {fv.readable_size}
                    </span>
                  </div>
                </span>
              </div>
              {(!share && !readOnly) && (
                <div className="file-version-actions">
                  {(
                    fv.comments_count &&
                    fv.comments_count.all > 0 &&
                    fv.comments_copied !== 'all' &&
                    !(fv.comments_copied === 'regular' && fv.comments_count.annotations === 0) &&
                    !(fv.comments_copied === 'annotations' && fv.comments_count.regular === 0)
                  ) && (
                    <>
                      <a
                        id={`vc-${fv.id}`}
                        href="#copy-comments"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          const modalParams = {
                            commentsCount: fv.comments_count ? fv.comments_count : null,
                            commentsCopied: fv.comments_copied ? fv.comments_copied : null,
                            file,
                            fileVersion: fv.id,
                            currentVersionId,
                            updateCommentsCount,
                          };
                          handleShowCommentsCopy('copy-comments', modalParams);
                        }}
                      >
                        <i className="far fa-comment-arrow-down" />
                      </a>
                      <UncontrolledTooltip target={`vc-${fv.id}`} boundariesElement="body">
                        {intl.formatMessage({ defaultMessage: 'Copy comments from this version to the newest version' })}
                      </UncontrolledTooltip>
                    </>
                  )}
                  {restoring === fv.id ? (
                    <Button
                      color="link"
                      size="sm"
                      id={`vr-loading-${fv.id}`}
                    >
                      <i className="fal fa-spinner fa-spin" />
                    </Button>
                  ) : (
                    <>
                      <Button
                        color="link"
                        size="sm"
                        id={`vr-${fv.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          performRestoreFileVersion(fv.id);
                        }}
                      >
                        <i className="far fa-rotate-right" />
                      </Button>
                      <UncontrolledTooltip target={`vr-${fv.id}`}>
                        {intl.formatMessage({ defaultMessage: 'Restore version' })}
                      </UncontrolledTooltip>
                    </>
                  )}
                  {deleting === fv.id ? (
                    <Button
                      color="link"
                      size="sm"
                      id={`vd-loading-${fv.id}`}
                    >
                      <i className="fal fa-spinner fa-spin" />
                    </Button>
                  ) : (
                    <>
                      <Button
                        color="link"
                        size="sm"
                        id={`vd-${fv.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          performDeleteFileVersion(fv.id);
                        }}
                      >
                        <i className="far fa-trash" />
                      </Button>
                      <UncontrolledTooltip target={`vd-${fv.id}`}>
                        {intl.formatMessage({ defaultMessage: 'Delete version' })}
                      </UncontrolledTooltip>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {file.versions.length === 0 && (
          <FileSidebarEmptyState
            info={intl.formatMessage({ defaultMessage: 'No file versions' })}
            wiki="https://help.stockpress.co/en/articles/5937566-file-versioning"
            video="https://player.vimeo.com/video/1075884943?h=468e697ff6&badge=0&byline=0&title=0"
            title={intl.formatMessage({ defaultMessage: 'File versions' })}
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  modal: state.App.modal,
  share: state.Shares.share,
});

const mapDispatchToProps = dispatch => ({
  handleCreateVersion: (fileUuid, params) => (
    dispatch(createFileVersion(fileUuid, params))
  ),
  handleDeleteVersion: (fileUuid, versionId) => (
    dispatch(deleteFileVersion(fileUuid, versionId))
  ),
  handleRestoreVersion: (fileUuid, versionId) => (
    dispatch(restoreFileVersion(fileUuid, versionId))
  ),
  handleShowCommentsCopy: (mode, modalParams) => {
    dispatch(showModal('CommentsCopyModal', {
      className: 'modal-comments-copy',
      mode,
      ...modalParams,
    }));
  },
});

FileVersions.propTypes = {
  file: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  handleCreateVersion: PropTypes.func.isRequired,
  handleDeleteVersion: PropTypes.func.isRequired,
  handleRestoreVersion: PropTypes.func.isRequired,
  currentVersionId: PropTypes.number,
  share: PropTypes.object,
  afterCreateVersion: PropTypes.func,
  switchVersion: PropTypes.func.isRequired,
  handleShowCommentsCopy: PropTypes.func.isRequired,
  updateCommentsCount: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

FileVersions.defaultProps = {
  currentVersionId: null,
  share: null,
  readOnly: false,
  afterCreateVersion: () => { },
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(FileVersions));
